import React, {useEffect, useRef, useState} from 'react'
import _ from 'lodash'
import dayjs from 'dayjs'
import classNames from 'classnames'
import {Button, DatePicker, InputNumber, message, Select} from 'antd'
import {FaFileExport, FaFileImport} from 'react-icons/fa6'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {callApi} from 'modules/asyncCache/callApi'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {getResponseItems} from 'views/Shared'
import {gConfigs} from 'views/AppLocals/configs'
import {
    course_class_getAttendances_Api,
    courseImportExport_applyImport_Api,
    courseImportExport_exportTemplate_Api,
    courseImportExport_previewImport_Api,
} from 'apis/course'
import {
    ClassSubjectPicker,
    CourseClassPicker,
    InfoBanner,
    ModeToggle,
    PreviewRowCard,
    RequiredMark,
} from './CourseImportExportShared'
import {ATTENDANCE_STATUS_OPTIONS, SESSION_IMPORT_MODES} from './course_import_export_config'

const ATTENDANCE_MODE_OPTIONS = [
    {value: SESSION_IMPORT_MODES.INSERT, label: 'attendance mode new session'},
    {value: SESSION_IMPORT_MODES.UPSERT, label: 'attendance mode update session'},
]

// Chọn buổi điểm danh ĐÃ CÓ - dùng chung Xuất (chọn để lấy dữ liệu hiện tại) & Nhập (bắt buộc chọn
// đúng buổi để đối chiếu SESSION_TITLE trong file). Load qua useAsyncAction (KHÔNG useAsync - đúng
// gotcha đã ghi ở StudentPanels.js), lọc theo subject_id đã chọn.
const ExistingSessionPicker = ({courseClass, subject, value, onChange, t}) => {
    const {response, isLoading, handleAsyncAction: fetchAttendances} = useAsyncAction({
        apiInfo: course_class_getAttendances_Api,
    })
    const ready = !!courseClass?.id && !!subject?.id
    useEffect(() => {
        if (!ready) return
        fetchAttendances({subject_id: subject.id, sort_by: 'date desc'}, {id: courseClass.id})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, courseClass?.id, subject?.id])
    const items = ready ? getResponseItems(response) : []

    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('attendance session to update')} <RequiredMark/></span>
            {ready ? (
                <Select
                    size="large" allowClear loading={isLoading}
                    placeholder={t('select attendance session placeholder')}
                    value={value?.id}
                    onChange={(id) => onChange(items.find((i) => i.id === id) || null)}
                    options={items.map((i) => ({
                        value: i.id,
                        label: `${i.title}${i.attendance_date ? ` (${dayjs(i.attendance_date).format('DD/MM/YYYY')})` : ''}`,
                    }))}
                    notFoundContent={<span className="text-xs text-color-300 italic">{t('no attendance session found')}</span>}
                />
            ) : (
                <div className="h-[46px] flex items-center px-3 rounded-lg border border-dashed border-color-200 text-xs text-color-300 italic">
                    {t('select subject first hint')}
                </div>
            )}
        </div>
    )
}

// ─── Tab Xuất ───────────────────────────────────────────────────────────────────────────────────
export const AttendanceExportPanel = ({entity, owner_id, owner_type, t}) => {
    const [mode, setMode] = useState(SESSION_IMPORT_MODES.INSERT)
    const [course, setCourse] = useState(null)
    const [courseClass, setCourseClass] = useState(null)
    const [subject, setSubject] = useState(null)

    // mode=insert - field định danh buổi MỚI.
    const [sessionTitle, setSessionTitle] = useState('')
    const [sessionTitleTouched, setSessionTitleTouched] = useState(false)
    const [attendanceDate, setAttendanceDate] = useState(dayjs())
    const [sessionTotalPeriods, setSessionTotalPeriods] = useState(1)
    const [defaultStatus, setDefaultStatus] = useState('PRESENT')

    // Tên buổi mặc định = "{ngày} - {tên môn}" (đúng convention màn điểm danh thủ công) - tự cập
    // nhật khi ngày/môn đổi, TRỪ khi user đã tự gõ (sessionTitleTouched).
    useEffect(() => {
        if (sessionTitleTouched || !subject?.title) return
        setSessionTitle(`${attendanceDate.format('DD/MM/YYYY')} - ${subject.title}`)
    }, [subject?.title, attendanceDate, sessionTitleTouched])

    // mode=upsert - buổi đã chọn để cập nhật.
    const [selectedAttendance, setSelectedAttendance] = useState(null)

    const hasCourseClassSubject = !!course?.id && !!courseClass?.id && !!subject?.id

    const {handleAsyncAction: doExport, loading: exporting} = useAsyncAction({
        apiInfo: courseImportExport_exportTemplate_Api,
        onSuccess: (result, {response}) => {
            const url = _.get(response, 'data.url')
            if (url) window.open(url, '_blank')
            message.success(t('import export template exported'))
        },
        onError: (err) => message.error(_.get(err, 'response.data.errors.message', t('error occurred'))),
    })

    const canExport = hasCourseClassSubject && (
        mode === SESSION_IMPORT_MODES.INSERT
            ? !!sessionTitle && !!attendanceDate && sessionTotalPeriods > 0
            : !!selectedAttendance?.id
    )

    const handleExport = () => {
        const baseParams = {course_id: course.id, course_class_id: courseClass.id, subject_id: subject.id, mode}
        const params = mode === SESSION_IMPORT_MODES.INSERT
            ? {
                ...baseParams,
                session_title: sessionTitle,
                attendance_date: attendanceDate.format('DD/MM/YYYY'),
                session_total_periods: sessionTotalPeriods,
                default_status: defaultStatus,
            }
            : {...baseParams, attendance_id: selectedAttendance.id}
        doExport(params, {type: owner_type, id: owner_id, key: entity.key})
    }

    return (
        <div className="flex flex-col gap-3">
            <InfoBanner>{t('course attendance export hint')}</InfoBanner>

            <CourseClassPicker owner_id={owner_id} owner_type={owner_type} course={course} setCourse={setCourse} courseClass={courseClass} setCourseClass={setCourseClass} t={t}/>
            <ClassSubjectPicker courseClass={courseClass} subject={subject} setSubject={setSubject} t={t}/>

            <ModeToggle value={mode} onChange={setMode} options={ATTENDANCE_MODE_OPTIONS} t={t}/>

            {mode === SESSION_IMPORT_MODES.INSERT ? (
                <>
                    <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('attendance session title')} <RequiredMark/></span>
                        <input
                            className="h-[46px] px-3 rounded-lg border border-color-200 text-sm background focus:border-amber-400 focus:outline-none"
                            value={sessionTitle}
                            onChange={(e) => {setSessionTitle(e.target.value); setSessionTitleTouched(true)}}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col gap-1.5">
                            <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('attendance date')} <RequiredMark/></span>
                            <DatePicker
                                size="large" className="w-full" allowClear={false}
                                format={gConfigs.dateFormat}
                                value={attendanceDate}
                                onChange={(value) => value && setAttendanceDate(value)}
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('session total periods')} <RequiredMark/></span>
                            <InputNumber size="large" className="w-full" min={1} max={20} value={sessionTotalPeriods} onChange={setSessionTotalPeriods}/>
                        </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-bold uppercase tracking-widest text-color-300">{t('default attendance status')}</span>
                        <Select
                            size="large" value={defaultStatus} onChange={setDefaultStatus}
                            options={ATTENDANCE_STATUS_OPTIONS.map((o) => ({value: o.value, label: t(o.label)}))}
                        />
                        <InfoBanner>{t('default attendance status hint')}</InfoBanner>
                    </div>
                </>
            ) : (
                <ExistingSessionPicker courseClass={courseClass} subject={subject} value={selectedAttendance} onChange={setSelectedAttendance} t={t}/>
            )}

            <Button
                type="primary" size="large" icon={<FaFileExport size={13}/>} loading={exporting}
                disabled={!canExport}
                onClick={handleExport}>
                {t(mode === SESSION_IMPORT_MODES.INSERT ? 'export blank template' : 'export selected template')}
            </Button>
        </div>
    )
}

// ─── Tab Nhập ───────────────────────────────────────────────────────────────────────────────────
const AttendancePreviewRow = ({row, t}) => (
    <PreviewRowCard row={row} title={row.student_name} t={t}>
        <span className="text-xs text-color-400">
            {row.session_title}
            {row.status_label ? ` · ${t(`attendance status ${row.status_label.toLowerCase()}`)}` : ''}
            {row.absent_reason ? ` · ${row.absent_reason}` : ''}
        </span>
    </PreviewRowCard>
)

export const AttendanceImportPanel = ({entity, owner_id, owner_type, t}) => {
    const inputRef = useRef(null)
    const [uploading, setUploading] = useState(false)
    const [preview, setPreview] = useState(null)
    const [mode, setMode] = useState(SESSION_IMPORT_MODES.INSERT)
    const [course, setCourse] = useState(null)
    const [courseClass, setCourseClass] = useState(null)
    const [subject, setSubject] = useState(null)
    const [selectedAttendance, setSelectedAttendance] = useState(null)

    const hasCourseClassSubject = !!course?.id && !!courseClass?.id && !!subject?.id
    const ready = mode === SESSION_IMPORT_MODES.INSERT ? hasCourseClassSubject : (hasCourseClassSubject && !!selectedAttendance?.id)

    const handleFile = (file) => {
        if (!file || uploading || !ready) return
        setUploading(true)
        callApi({
            apiInfo: courseImportExport_previewImport_Api,
            query: bindQueryParams([{type: owner_type}, {id: owner_id}, {key: entity.key}]),
            values: {
                file, course_id: course.id, course_class_id: courseClass.id, subject_id: subject.id,
                attendance_id: mode === SESSION_IMPORT_MODES.UPSERT ? selectedAttendance.id : undefined,
            },
        }).then((result) => {
            setUploading(false)
            if (inputRef.current) inputRef.current.value = ''
            if (!result) return
            if (result.error) {
                message.error(_.get(result, 'errorMessages.error') || t('error occurred'))
                return
            }
            setPreview(result.result)
        })
    }

    const {handleAsyncAction: doApply, loading: applying} = useAsyncAction({
        apiInfo: courseImportExport_applyImport_Api,
        onSuccess: (result, {response}) => {
            const data = _.get(response, 'data')
            message.success(`${t('import export applied')}: ${data?.applied ?? 0}${data?.failed ? ` (${t('error')}: ${data.failed})` : ''}`)
            setPreview(null)
        },
        onError: (err) => message.error(_.get(err, 'response.data.errors.message', t('error occurred'))),
    })

    if (!preview) {
        return (
            <div className="flex flex-col gap-3">
                <InfoBanner>{t('import export upload hint')}</InfoBanner>
                <CourseClassPicker owner_id={owner_id} owner_type={owner_type} course={course} setCourse={setCourse} courseClass={courseClass} setCourseClass={setCourseClass} t={t}/>
                <ClassSubjectPicker courseClass={courseClass} subject={subject} setSubject={setSubject} t={t}/>
                <ModeToggle value={mode} onChange={setMode} options={ATTENDANCE_MODE_OPTIONS} t={t}/>
                {mode === SESSION_IMPORT_MODES.UPSERT && (
                    <ExistingSessionPicker courseClass={courseClass} subject={subject} value={selectedAttendance} onChange={setSelectedAttendance} t={t}/>
                )}
                <label className={classNames(
                    'p-8 flex flex-col items-center gap-2 rounded-xl border-2 border-dashed transition-all',
                    ready ? 'border-amber-300 hover:bg-amber-50 cursor-pointer' : 'border-color-200 opacity-50 cursor-not-allowed')}>
                    <FaFileImport size={26} className="text-amber-600"/>
                    <span className="text-sm font-semibold text-color-000">
                        {!ready ? t('select course first hint') : (uploading ? t('uploading') : t('choose file to upload'))}
                    </span>
                    <input
                        ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden"
                        disabled={uploading || !ready}
                        onChange={(e) => handleFile(e.target.files?.[0])}
                    />
                </label>
            </div>
        )
    }

    const validCount = preview.rows.filter((r) => r.status === 1).length

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-color-000">
                    {t('import export summary')}: {preview.valid}/{preview.total}
                </span>
                <button type="button" onClick={() => setPreview(null)} className="text-xs text-amber-700 underline cursor-pointer flex-shrink-0">
                    {t('choose another file')}
                </button>
            </div>

            <div className="flex flex-col gap-2 max-h-96 overflow-y-auto">
                {preview.rows.map((row) => <AttendancePreviewRow key={row.id} row={row} t={t}/>)}
            </div>

            <Button
                type="primary" size="large" loading={applying} disabled={!validCount}
                onClick={() => doApply({batch_id: preview.batch_id}, {type: owner_type, id: owner_id, key: entity.key})}>
                {t('apply import')} ({validCount})
            </Button>
        </div>
    )
}
